import { type KeyboardEvent, useState } from "react";
import { useDeleteSubjectTask } from "../../api/useDeleteSubjectTask";
import { useGetSubjectTasksBySubTopicId } from "../../api/useGetSubjectTasksBySubTopicId";
import { usePostSubjectTask } from "../../api/usePostSubjectTask";
import { useUpdateSubjectTask } from "../../api/useUpdateSubjectTask";
import { usePermissions } from "../../../../hooks/usePermissions";

export function useTodoSection(subTopicId: string) {
  const { isAdmin } = usePermissions();
  const { data: tasks = [], isLoading, isError } = useGetSubjectTasksBySubTopicId(subTopicId, null);

  const { mutate: postTask, isPending: isAdding } = usePostSubjectTask(subTopicId);
  const { mutate: updateTask } = useUpdateSubjectTask(subTopicId);
  const { mutateAsync: deleteTask } = useDeleteSubjectTask(subTopicId);

  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  const addTask = (title: string) => {
    postTask({ title, position: tasks.length });
  };

  const toggleTask = ({ id, completed }: { id: number; completed: boolean }) => {
    updateTask({
      id,
      state: completed ? 'completed' : 'pending',
      completed_at: completed ? new Date().toISOString() : null,
    });
  };

  function handleAddTask() {
    if (newTaskTitle.trim()) {
      addTask(newTaskTitle.trim());
      setNewTaskTitle("");
    }
  }

  function handleNewTaskKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddTask();
    }
  }

  function handleStartEdit(id: number, title: string) {
    setEditingTaskId(id);
    setEditingTitle(title);
  }

  function handleCancelEdit() {
    setEditingTaskId(null);
  }

  function handleSaveEdit(id: number) {
    if (editingTitle.trim()) {
      updateTask({ id, title: editingTitle.trim() });
      setEditingTaskId(null);
    }
  }

  function handleEditKeyDown(id: number, event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveEdit(id);
    } else if (event.key === "Escape") {
      handleCancelEdit();
    }
  }

  const sortedTasks = [...tasks].sort((a, b) => {
    // 1. Sort by state: 'pending' before 'completed'
    if (a.state !== b.state) {
      return a.state === 'pending' ? -1 : 1;
    }
    // 2. If same state, sort by created_at ascending (oldest first)
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  const completedCount = tasks.filter((task) => task.state === 'completed').length;

  return {
    isAdmin,
    tasks: sortedTasks,
    isLoading,
    isError,
    isAdding,
    completedCount,
    newTaskTitle,
    setNewTaskTitle,
    editingTaskId,
    editingTitle,
    setEditingTitle,
    toggleTask,
    deleteTask,
    handleAddTask,
    handleNewTaskKeyDown,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleEditKeyDown,
  };
}
